import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"

export const CANDIDATES_LIST_SEARCH_STATE_KEY = "candidatesListSearch"
export const CANDIDATES_LIST_CONTEXT_STATE_KEY = "listContext"
export const CANDIDATES_RESTORE_LIST_CONTEXT_STATE_KEY = "restoreListContext"
const CANDIDATES_LIST_NAVIGATION_STORAGE_KEY = "candidatesListNavigationState"

function toNumber(value, fallback = 0) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function normalizeListContext(listContext) {
  if (!listContext) {
    return null
  }

  return {
    ...listContext,
    pageIndex: toNumber(listContext.pageIndex, 1),
    pageSize: toNumber(listContext.pageSize, 20),
    rowIndex: toNumber(listContext.rowIndex, 0),
    totalCount: toNumber(listContext.totalCount, 0),
    pageCandidateIds: (listContext.pageCandidateIds || []).map((id) =>
      Number(id)
    ),
    filters: listContext.filters || [],
    q: listContext.q || "",
    ordering: listContext.ordering || "",
  }
}

export function persistCandidatesListNavigationState(navigationState) {
  sessionStorage.setItem(
    CANDIDATES_LIST_NAVIGATION_STORAGE_KEY,
    JSON.stringify(navigationState)
  )
}

export function getPersistedCandidatesListNavigationState() {
  const raw = sessionStorage.getItem(CANDIDATES_LIST_NAVIGATION_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function getCandidatesNavigationState(location) {
  const locationState = location?.state
  if (locationState?.[CANDIDATES_LIST_CONTEXT_STATE_KEY]) {
    return {
      candidatesListSearch:
        locationState[CANDIDATES_LIST_SEARCH_STATE_KEY] ?? "",
      listContext: normalizeListContext(
        locationState[CANDIDATES_LIST_CONTEXT_STATE_KEY]
      ),
    }
  }

  const persisted = getPersistedCandidatesListNavigationState()
  if (persisted?.listContext) {
    return {
      candidatesListSearch: persisted.candidatesListSearch ?? "",
      listContext: normalizeListContext(persisted.listContext),
    }
  }

  return {
    candidatesListSearch: null,
    listContext: null,
  }
}

export function buildCandidatesListContext({
  filters,
  q,
  ordering,
  pageIndex,
  pageSize,
  rowIndex,
  pageCandidateIds,
  totalCount,
}) {
  return normalizeListContext({
    filters,
    q,
    ordering,
    pageIndex,
    pageSize,
    rowIndex,
    pageCandidateIds,
    totalCount,
  })
}

export function getCandidatesListGlobalIndex(listContext) {
  if (!listContext) {
    return null
  }
  return (
    (listContext.pageIndex - 1) * listContext.pageSize +
    listContext.rowIndex +
    1
  )
}

export function canGoToPreviousCandidate(listContext) {
  if (!listContext || listContext.rowIndex < 0) {
    return false
  }
  const globalIndex = getCandidatesListGlobalIndex(listContext)
  return globalIndex > 1
}

export function canGoToNextCandidate(listContext) {
  if (!listContext || listContext.rowIndex < 0) {
    return false
  }
  const globalIndex = getCandidatesListGlobalIndex(listContext)
  return globalIndex < listContext.totalCount
}

export function buildCandidateDetailsNavigationState(
  locationSearch,
  listContext
) {
  return {
    [CANDIDATES_LIST_SEARCH_STATE_KEY]: locationSearch ?? "",
    [CANDIDATES_LIST_CONTEXT_STATE_KEY]: normalizeListContext(listContext),
  }
}

export function navigateToCandidateDetails(
  navigate,
  locationSearch,
  listContext,
  candidateId
) {
  const navigationState = buildCandidateDetailsNavigationState(
    locationSearch,
    listContext
  )
  persistCandidatesListNavigationState(navigationState)
  navigate(`/candidates/${candidateId}`, {
    state: navigationState,
  })
}

export function navigateBackToCandidatesList(navigate, location) {
  const { candidatesListSearch, listContext } =
    getCandidatesNavigationState(location)
  const search = candidatesListSearch ?? ""

  navigate(`/candidates${search}`, {
    state: listContext
      ? { [CANDIDATES_RESTORE_LIST_CONTEXT_STATE_KEY]: listContext }
      : undefined,
  })
}

async function fetchCandidatesPage(axios, listContext, pageIndex) {
  return axios.post(
    "/clients/api/candidates/",
    { filters: listContext.filters },
    {
      params: {
        q: listContext.q,
        page: pageIndex,
        page_size: listContext.pageSize,
        ordering: listContext.ordering,
      },
    }
  )
}

export async function resolveAdjacentCandidate(axios, listContext, direction) {
  const normalizedContext = normalizeListContext(listContext)
  const { rowIndex, pageCandidateIds } = normalizedContext

  if (rowIndex < 0 || !pageCandidateIds.length) {
    return null
  }

  if (direction === "next") {
    if (rowIndex < pageCandidateIds.length - 1) {
      const newRowIndex = rowIndex + 1
      return {
        candidateId: pageCandidateIds[newRowIndex],
        listContext: {
          ...normalizedContext,
          rowIndex: newRowIndex,
        },
      }
    }

    const globalIndex = getCandidatesListGlobalIndex(normalizedContext)
    if (globalIndex >= normalizedContext.totalCount) {
      return null
    }

    const nextPageIndex = normalizedContext.pageIndex + 1
    const { data } = await fetchCandidatesPage(
      axios,
      normalizedContext,
      nextPageIndex
    )
    if (!data.results?.length) {
      return null
    }

    return {
      candidateId: data.results[0].id,
      listContext: {
        ...normalizedContext,
        pageIndex: nextPageIndex,
        rowIndex: 0,
        pageCandidateIds: data.results.map((result) => Number(result.id)),
        totalCount: toNumber(data.count, normalizedContext.totalCount),
      },
    }
  }

  if (direction === "prev") {
    if (rowIndex > 0) {
      const newRowIndex = rowIndex - 1
      return {
        candidateId: pageCandidateIds[newRowIndex],
        listContext: {
          ...normalizedContext,
          rowIndex: newRowIndex,
        },
      }
    }

    if (normalizedContext.pageIndex <= 1) {
      return null
    }

    const prevPageIndex = normalizedContext.pageIndex - 1
    const { data } = await fetchCandidatesPage(
      axios,
      normalizedContext,
      prevPageIndex
    )
    if (!data.results?.length) {
      return null
    }

    const lastIndex = data.results.length - 1
    return {
      candidateId: data.results[lastIndex].id,
      listContext: {
        ...normalizedContext,
        pageIndex: prevPageIndex,
        rowIndex: lastIndex,
        pageCandidateIds: data.results.map((result) => Number(result.id)),
        totalCount: toNumber(data.count, normalizedContext.totalCount),
      },
    }
  }

  return null
}

export function useCandidateListNavigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const axios = useAuthorizedAxiosClient()
  const [isNavigatingAdjacent, setIsNavigatingAdjacent] = useState(false)
  const [adjacentNavigationError, setAdjacentNavigationError] = useState(false)

  const { candidatesListSearch, listContext } =
    getCandidatesNavigationState(location)
  const globalIndex = getCandidatesListGlobalIndex(listContext)
  const positionLabel =
    listContext && globalIndex != null
      ? `${globalIndex} of ${listContext.totalCount}`
      : null

  async function goToAdjacent(direction) {
    if (!listContext || isNavigatingAdjacent) {
      return
    }

    setAdjacentNavigationError(false)
    setIsNavigatingAdjacent(true)
    try {
      const result = await resolveAdjacentCandidate(
        axios,
        listContext,
        direction
      )
      if (!result) {
        setAdjacentNavigationError(true)
        return
      }
      navigateToCandidateDetails(
        navigate,
        candidatesListSearch ?? "",
        result.listContext,
        result.candidateId
      )
    } catch {
      setAdjacentNavigationError(true)
    } finally {
      setIsNavigatingAdjacent(false)
    }
  }

  return {
    canGoPrevious: canGoToPreviousCandidate(listContext),
    canGoNext: canGoToNextCandidate(listContext),
    hasListContext: !!listContext,
    positionLabel,
    isNavigatingAdjacent,
    adjacentNavigationError,
    goBackToList: () => navigateBackToCandidatesList(navigate, location),
    goToPrevious: () => goToAdjacent("prev"),
    goToNext: () => goToAdjacent("next"),
  }
}
